import { useEffect, useRef, useState } from 'react';
import { reservationBgImage, contactPhone } from '@/mocks/homeData';
import { supabase } from '@/lib/supabase';

export default function ReservationSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('contact_alt') as string)?.trim();
    if (honeypot) {
      setFormStatus('success');
      setIsSubmitting(false);
      return;
    }

    const name = (formData.get('name') as string)?.trim();
    const phone = (formData.get('phone') as string)?.trim();
    const date = (formData.get('date') as string)?.trim();
    const unit = (formData.get('unit') as string)?.trim();
    const message = (formData.get('message') as string)?.trim();

    if (!name || !phone) {
      setFormError('이름과 연락처는 필수 입력 항목입니다.');
      setFormStatus('error');
      setIsSubmitting(false);
      return;
    }

    const phoneDigits = phone.replace(/-/g, '');
    const phoneRegex = /^01[016789]\d{7,8}$/;
    if (!phoneRegex.test(phoneDigits)) {
      setFormError('올바른 연락처를 입력해주세요.');
      setFormStatus('error');
      setIsSubmitting(false);
      return;
    }

    const reservationData = {
      name,
      phone,
      date: date || '',
      unit: unit || '',
      message: message || '',
    };

    // ── 1. Readdy Form 제출 ──
    let formSubmitOk = false;
    let formSubmitError = '';
    try {
      const params = new URLSearchParams();
      params.append('name', name);
      params.append('phone', phone);
      params.append('date', date || '');
      params.append('unit', unit || '');
      params.append('message', message || '');

      const response = await fetch('https://readdy.ai/api/form/d99j85qg3iubr452imu0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverCode = parsed?.code as string | undefined;
      const serverMsg =
        (parsed?.meta as Record<string, string> | undefined)?.message ||
        (parsed?.meta as Record<string, string> | undefined)?.detail ||
        (parsed?.message as string) ||
        responseText;

      if (response.ok && serverCode === 'OK') {
        formSubmitOk = true;
      } else if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
        formSubmitError = '제출에 문제가 발생했습니다. 다시 시도해주세요.';
      } else {
        formSubmitError = serverMsg || '폼 제출 중 오류가 발생했습니다.';
      }
    } catch (err) {
      formSubmitError = err instanceof Error ? err.message : '폼 제출 중 네트워크 오류가 발생했습니다.';
    }

    // ── 2. 카카오 채널톡 실시간 알림 ──
    if (window.ChannelIO) {
      try {
        window.ChannelIO('track', 'reservation_submitted', {
          이름: name,
          연락처: phone,
          희망방문일: date || '미입력',
          관심평형: unit || '미선택',
          문의사항: message || '없음',
        });
        console.log('[ChannelTalk] reservation_submitted 이벤트 전송 완료');
      } catch (err) {
        console.error('[ChannelTalk] 이벤트 전송 실패:', err);
      }
    } else {
      console.warn('[ChannelTalk] ChannelIO 객체가 없습니다. SDK가 로드되지 않았을 수 있습니다.');
    }

    // ── 3. Resend 이메일 알림 ──
    supabase.functions.invoke('send-reservation-email', {
      body: reservationData,
    }).then(({ data, error }) => {
      if (error) {
        console.error('[Resend] 이메일 발송 실패:', error);
      } else {
        console.log('[Resend] 이메일 발송 성공:', data);
      }
    }).catch((err) => {
      console.error('[Resend] Edge Function 호출 실패:', err);
    });

    // ── 4. CoolSMS 카카오 알림톡 + SMS 폴백 ──
    supabase.functions.invoke('kakao-notify', {
      body: {
        name,
        phone,
        date: date || '',
        time_slot: '',
        topic: unit || '미선택',
        memo: message || '없음',
      },
    }).then(({ data, error }) => {
      if (error) {
        console.error('[KakaoNotify] 알림톡/SMS 발송 실패:', error);
      } else {
        console.log('[KakaoNotify] 알림톡/SMS 발송 성공:', data);
      }
    }).catch((err) => {
      console.error('[KakaoNotify] Edge Function 호출 실패:', err);
    });

    // ── 결과 표시 ──
    if (formSubmitOk) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      setCharCount(0);
    } else {
      setFormStatus('error');
      setFormError(formSubmitError || '폼 제출 중 오류가 발생했습니다.');
    }
    setIsSubmitting(false);
  };

  return (
    <section id="reservation" ref={sectionRef} className="relative w-full">
      <div className="flex flex-col lg:flex-row min-h-[600px] lg:min-h-[700px]">
        <div className="relative w-full lg:w-1/2 min-h-[300px] lg:min-h-full">
          <img
            src={reservationBgImage}
            alt="신상도역 이안 아파트 외부투시도"
            className="absolute inset-0 w-full h-full object-contain object-center"
            width={800}
            height={900}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent" />
          <div className="relative z-10 flex flex-col justify-between h-full p-6 md:p-10 lg:p-12">
          </div>
        </div>

        <div className="w-full lg:w-1/2 bg-background-50 flex items-center justify-center p-6 md:p-10 lg:p-16">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h2 className="reveal opacity-0 font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3 text-balance">
                방문예약 신청
              </h2>
              <p className="reveal opacity-0 text-foreground-600 text-base md:text-lg leading-relaxed">
                원하시는 일정에 맞춰 전문 상담사가
                <br />
                친절하게 안내해 드립니다
              </p>
            </div>

            <form onSubmit={handleSubmit} className="reveal opacity-0 space-y-4" data-readdy-form>
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <div>
                <label htmlFor="name" className="block text-base font-medium text-foreground-700 mb-1.5">
                  이름 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-base focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="이름을 입력해주세요"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-base font-medium text-foreground-700 mb-1.5">
                  연락처 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-base focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                  placeholder="010-0000-0000"
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-base font-medium text-foreground-700 mb-1.5">
                  희망 방문일
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-base focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label htmlFor="unit" className="block text-base font-medium text-foreground-700 mb-1.5">
                  관심 평형
                </label>
                <select
                  id="unit"
                  name="unit"
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-base focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                >
                  <option value="">평형을 선택해주세요</option>
                  <option value="59">59㎡ 타입 (24평형) - 315세대</option>
                  <option value="71">71㎡ 타입 (28평형) - 37세대</option>
                  <option value="84">84㎡ 타입 (33평형) - 107세대</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-base font-medium text-foreground-700 mb-1.5">
                  문의사항
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-base focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                  placeholder="문의하실 내용을 입력해주세요"
                />
                <p className="text-sm text-foreground-400 mt-1 text-right">{charCount} / 500자</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-foreground-900 text-background-50 text-lg font-semibold whitespace-nowrap transition-all hover:bg-foreground-800 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    접수 중...
                  </>
                ) : (
                  <>
                    방문예약 신청하기
                    <i className="ri-arrow-right-line" />
                  </>
                )}
              </button>

              <a
                href={`tel:${contactPhone}`}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border-2 border-foreground-200 text-foreground-700 text-lg font-semibold whitespace-nowrap transition-all hover:bg-foreground-50 hover:border-foreground-300"
              >
                <i className="ri-phone-line" />
                전화상담 {contactPhone}
              </a>

              {formStatus === 'success' && (
                <div className="p-4 rounded-xl bg-secondary-100 text-secondary-800 text-base text-center">
                  <i className="ri-check-line mr-1" />
                  방문예약 신청이 접수되었습니다. 빠른 시일 내에 연락드리겠습니다.
                </div>
              )}

              {formStatus === 'error' && formError && (
                <div className="p-4 rounded-xl bg-accent-100 text-accent-800 text-base text-center">
                  <i className="ri-error-warning-line mr-1" />
                  {formError}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}